/** Prefer team assignees (from filter groups / visible tickets) in pickers. */

export type AssigneeFilterGroup = {
  id: string;
  filters: Array<{ assignees?: string[] }>;
};

function uniqSorted(names: Iterable<string>): string[] {
  return Array.from(new Set(Array.from(names).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, 'cs')
  );
}

/** Collect assignee names configured on filter groups (= team lists). */
export function assigneesFromFilterGroups(
  filterGroups: AssigneeFilterGroup[] | undefined,
  selectedGroupId?: string | null
): string[] {
  if (!filterGroups?.length) return [];

  const groups =
    selectedGroupId != null
      ? filterGroups.filter((g) => g.id === selectedGroupId)
      : filterGroups;

  // If selected group has no assignee filter, fall back to all groups
  const named = new Set<string>();
  for (const g of groups) {
    for (const f of g.filters || []) {
      (f.assignees || []).forEach((a) => named.add(a));
    }
  }
  if (named.size === 0 && selectedGroupId != null) {
    for (const g of filterGroups) {
      for (const f of g.filters || []) {
        (f.assignees || []).forEach((a) => named.add(a));
      }
    }
  }
  return uniqSorted(named);
}

/**
 * Split assignees into preferred (team) vs others.
 * Preferred = filter-group assignees ∪ visible ticket assignees ∪ current.
 */
export function splitAssigneesForPicker(
  allAssignees: string[],
  opts: {
    filterGroups?: AssigneeFilterGroup[];
    selectedGroupId?: string | null;
    visibleAssignees?: string[];
    currentAssignee?: string | null;
  } = {}
): { preferred: string[]; others: string[] } {
  const team = new Set(
    assigneesFromFilterGroups(opts.filterGroups, opts.selectedGroupId)
  );
  (opts.visibleAssignees || []).forEach((a) => team.add(a));
  if (opts.currentAssignee) team.add(opts.currentAssignee);

  const all = uniqSorted([
    ...allAssignees,
    ...(opts.currentAssignee ? [opts.currentAssignee] : []),
  ]);

  // If we have no team signal at all, keep everyone preferred (no weird empty list)
  if (team.size === 0) {
    return { preferred: all, others: [] };
  }

  const preferred = all.filter((a) => team.has(a));
  const others = all.filter((a) => !team.has(a));
  return { preferred, others };
}
